import React, { useCallback, useMemo } from "react";

import { useTranslation } from "react-i18next";

import { ComboboxBase } from "@/src/components/form-fields/Combobox";
import { FieldDescription } from "@/src/components/ui/field";

import type { JSX } from "react";

import type { ComboboxOption } from "@/src/components/form-fields/MultiCombobox";

import type { EmbedProfileSelectProps } from "./EmbedProfileSelect.types";

// Sentinel for the "no profile" option: Combobox values are strings
const NULL_OPTION_VALUE = "";

export default function EmbedProfileSelect({
  value,
  optionList,
  disabled,
  isLoading,
  isError,
  popoverContainer,
  description,
  onValueChange,
}: EmbedProfileSelectProps): JSX.Element {
  const { t } = useTranslation("editor-content");

  const defaultProfile = optionList.find((profile) => profile.default);
  const nullLabel = defaultProfile
    ? t("embedProfile.inheritDefault", { label: defaultProfile.label })
    : t("embedProfile.noRestriction");

  const isUnavailable = isLoading || isError;
  const placeholder = isUnavailable
    ? t(isLoading ? "embedProfile.loading" : "embedProfile.loadError")
    : nullLabel;

  const comboboxOptionList = useMemo<ComboboxOption[]>(
    () =>
      isUnavailable
        ? []
        : [
            { value: NULL_OPTION_VALUE, label: nullLabel },
            ...optionList.map((profile) => ({
              value: profile.uuid,
              label: profile.label,
            })),
          ],
    [optionList, nullLabel, isUnavailable],
  );

  const knownProfileUuid = optionList.find(
    (profile) => profile.uuid === value,
  )?.uuid;
  const comboboxValue = isUnavailable
    ? undefined
    : (knownProfileUuid ?? NULL_OPTION_VALUE);

  const handleValueChange = useCallback(
    (nextValue: string): void => {
      onValueChange(nextValue === NULL_OPTION_VALUE ? null : nextValue);
    },
    [onValueChange],
  );

  return (
    <div className="flex flex-col gap-2">
      <ComboboxBase
        disabled={disabled}
        emptyLabel={t("embedProfile.empty")}
        isLoading={isLoading}
        label={t("embedProfile.label")}
        loadingLabel={t("embedProfile.loading")}
        onValueChange={handleValueChange}
        options={comboboxOptionList}
        placeholder={placeholder}
        popoverContainer={popoverContainer}
        searchPlaceholder={t("embedProfile.search")}
        value={comboboxValue}
      />
      {description ? <FieldDescription>{description}</FieldDescription> : null}
    </div>
  );
}
